import type {
  BuyerLeadStatus,
  BuyerSubmissionStatus,
  QualificationVerdict,
} from "@/api/buyers";
import type { StatusTone } from "@/design-system/components/ui";

// Presentation helpers shared by the four Buyer CRM list pages. Kept out of the
// pages so a status reads the same colour everywhere — a buyer shown as "at
// risk" on one screen and "fine" on another is worse than either.

/**
 * Funnel status → badge tone.
 *
 * The three terminal statuses are `muted` rather than `danger`: closing a buyer
 * out as Not qualified or Nurture is an ordinary, correct outcome, and painting
 * it red would make a well-worked book look like a wall of failures.
 */
export function buyerStatusTone(status: BuyerLeadStatus): StatusTone {
  switch (status) {
    case "New":
      return "info";
    case "Assigned":
    case "AttemptingContact":
      return "neutral";
    case "Engaged":
    case "ActivelySubmitting":
      return "progress";
    case "Qualified":
    case "ConvertedToBorrower":
    case "ClosedPurchased":
      return "ok";
    case "NotQualified":
    case "Nurture":
    case "Dead":
      return "muted";
  }
}

/** Qualification verdict → tone. `Unknown` is `neutral`, not a pass: an
 *  unassessed dimension must not read as cleared. */
export function verdictTone(verdict: QualificationVerdict): StatusTone {
  switch (verdict) {
    case "Pass":
      return "ok";
    case "Concern":
      return "warn";
    case "Fail":
      return "danger";
    case "Unknown":
      return "neutral";
  }
}

/**
 * The submission stage groups (WSC) — what a status pill's colour says at a
 * glance: early, waiting on the broker, moving forward, won, or stopped.
 */
export type SubmissionStageGroup = "early" | "waiting" | "moving" | "won" | "stopped";

/**
 * 🔴 THE ONE status → group table. Every submission status pill reads it
 * through {@link submissionStatusTone}; a `Record` over the union makes a new
 * status a compile error until it is placed, and the parity test asserts the
 * server's enum (via the shared fixture) maps to exactly one group.
 *
 * <p>Placed by judgement (not named in the brief): BrokerResponded → early (the
 * broker has replied and the package is not yet built), InfoReceived and
 * CallMeeting → moving (diligence under way).</p>
 */
export const SUBMISSION_STAGE_GROUP: Record<BuyerSubmissionStatus, SubmissionStageGroup> = {
  Shortlisted: "early",
  BuyerApproved: "early",
  OutreachSent: "early",
  BrokerResponded: "early",
  PackageGenerated: "waiting",
  PackageSent: "waiting",
  InfoReceived: "moving",
  CallMeeting: "moving",
  OfferLoi: "moving",
  UnderContract: "moving",
  // Escalation PR-2 — a live Phoenix deal is progress, never "stopped".
  EscalatedToDeal: "moving",
  ClosedPurchased: "won",
  DeclinedByBuyer: "stopped",
  NoResponse: "stopped",
  RejectedByBroker: "stopped",
  Passed: "stopped",
  Dead: "stopped",
};

/**
 * Group → tone. 🔴 Never `danger`: red stays reserved for the needs-action
 * chips (Follow-up overdue), and closing a submission out is an ordinary,
 * correct outcome — `muted` keeps a well-worked book from reading as a wall of
 * failures. Every tone's text clears AA on its own fill (design-tokens.css).
 */
export const SUBMISSION_STAGE_TONE: Record<SubmissionStageGroup, StatusTone> = {
  early: "infoInk",
  waiting: "warn",
  moving: "teal",
  won: "okInk",
  stopped: "muted",
};

/** Submission status → pill tone, through the one stage-group table. */
export function submissionStatusTone(status: BuyerSubmissionStatus): StatusTone {
  return SUBMISSION_STAGE_TONE[SUBMISSION_STAGE_GROUP[status]] ?? "neutral";
}
